import { api } from "./api";


// Strict "YYYY-MM-DD" check — rejects malformed strings and out-of-range values (e.g.
// 2024-13-40) that a loose `new Date(s)` parse would otherwise silently roll over.
export function isValidDateString(s: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return false;
  const [, yStr, mStr, dStr] = match;
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const parsed = new Date(y, m - 1, d);
  return (
    parsed.getFullYear() === y &&
    parsed.getMonth() === m - 1 &&
    parsed.getDate() === d
  );
}

export function formatVolume(v: number): string {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "m";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "k";
  return String(Math.round(v));
}

  export function unixTimestampToNewYork(timestamp: number): [string, Date] {
    const date = new Date(timestamp * 1000);
    // convert to New York time, format as 17 Apr 2025 HH:MM
    const newyorkDate: Date = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );

    const day = String(newyorkDate.getDate()).padStart(2, "0");
    const month = String(newyorkDate.getMonth() + 1).padStart(2, "0");
    const year = newyorkDate.getFullYear();
    const hours = String(newyorkDate.getHours()).padStart(2, "0");
    const minutes = String(newyorkDate.getMinutes()).padStart(2, "0");
    const seconds = String(newyorkDate.getSeconds()).padStart(2, "0");

    // get the day of the week
    const options: Intl.DateTimeFormatOptions = { weekday: "short" };
    const dayOfWeek = newyorkDate.toLocaleDateString("en-US", options);

    return [
      `${year}-${month}-${day} ${hours}:${minutes} (${dayOfWeek})`,
      newyorkDate,
    ];
  }

  // Formats an ISO timestamp string as "YYYY-MM-DD HH:MM (Weekday)" in America/New_York,
  // matching the News Search timestamp display.
  export function formatIsoTimestampNYC(ts: string | null): string {
    if (!ts) return "";
    const normalized = ts.includes("+") || ts.toUpperCase().includes("Z") ? ts : ts + "Z";
    const date = new Date(normalized);
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      weekday: "short",
      hour12: false,
    });
    const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} (${parts.weekday})`;
  }

  export function resolveEndpoint() : string {
    // return "http://localhost:8000";
    // return "https://api.reflexia.markets";
    return "/api";
  }

const OPENAI_KEY_CACHE_KEY = "openai_key_cache";

async function fetchOpenAIKeyFromBackend(): Promise<string> {
  const res = await api.get<{ key?: string; openai_key?: string } | string>("/openai-key");
  const data = res.data;
  const key: string = typeof data === "string" ? data : (data.key ?? data.openai_key ?? "");
  sessionStorage.setItem(OPENAI_KEY_CACHE_KEY, key);
  return key;
}

async function getOpenAIKey(): Promise<string> {
  const cached = sessionStorage.getItem(OPENAI_KEY_CACHE_KEY);
  if (cached) {
    // Async refresh in background — don't await
    fetchOpenAIKeyFromBackend().catch(() => {});
    return cached;
  }
  return fetchOpenAIKeyFromBackend();
}

// Singleton AudioContext — must be resumed inside a user gesture once, then stays unlocked
let _audioCtx: AudioContext | null = null;
function getAudioContext(): AudioContext {
  if (!_audioCtx || _audioCtx.state === "closed") {
    _audioCtx = new AudioContext();
  }
  return _audioCtx;
}

// Global playback state — only one active read at a time
interface ActivePlayback {
  text: string;
  cancel: () => void;
  paused: boolean;
  // For MediaSource path
  audio?: HTMLAudioElement;
  // For Web Audio path
  webAudioSource?: AudioBufferSourceNode;
}
let _active: ActivePlayback | null = null;

export async function speakText(text: string): Promise<void> {
  if (_active) {
    if (_active.text === text) {
      // Same text — toggle pause/resume
      const audioCtx = getAudioContext();
      if (_active.paused) {
        audioCtx.resume();
        if (_active.audio) _active.audio.play().catch(() => {});
        _active.paused = false;
      } else {
        audioCtx.suspend();
        if (_active.audio) _active.audio.pause();
        _active.paused = true;
      }
      return;
    }
    // Different text — cancel current
    _active.cancel();
    _active = null;
  }

  // Unlock / resume AudioContext synchronously while still inside the user-gesture stack.
  const audioCtx = getAudioContext();
  const resumePromise = audioCtx.state === "suspended" ? audioCtx.resume() : Promise.resolve();

  const [apiKey] = await Promise.all([getOpenAIKey(), resumePromise]);

  // Check if cancelled while awaiting the key
  let cancelled = false;
  const entry: ActivePlayback = {
    text,
    paused: false,
    cancel: () => { cancelled = true; },
  };
  _active = entry;

  const response = await fetch("https://api.openai.com/v1/audio/speech", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: "gpt-4o-mini-tts",
      input: text,
      voice: "nova",
      speed: 1.2,
      response_format: "mp3",
    }),
  });

  if (!response.ok || !response.body) {
    if (_active === entry) _active = null;
    throw new Error(`OpenAI TTS error: ${response.status}`);
  }

  if (cancelled) return;

  const supportsMediaSourceMp3 =
    typeof MediaSource !== "undefined" &&
    MediaSource.isTypeSupported("audio/mpeg");

  try {
    if (supportsMediaSourceMp3) {
      await streamWithMediaSource(response.body, entry, () => cancelled);
    } else {
      await playWithWebAudio(response.body, audioCtx, entry, () => cancelled);
    }
  } finally {
    if (_active === entry) _active = null;
  }
}

async function streamWithMediaSource(
  body: ReadableStream<Uint8Array>,
  entry: ActivePlayback,
  isCancelled: () => boolean,
): Promise<void> {
  const mediaSource = new MediaSource();
  const audio = new Audio();
  entry.audio = audio;
  entry.cancel = () => {
    audio.pause();
    audio.src = "";
    reader.cancel().catch(() => {});
    (entry as any)._cancelled = true;
  };
  audio.src = URL.createObjectURL(mediaSource);

  let reader!: ReadableStreamDefaultReader<Uint8Array>;

  return new Promise<void>((resolve, reject) => {
    mediaSource.addEventListener("sourceopen", async () => {
      const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
      const queue: ArrayBuffer[] = [];
      let appending = false;
      let streamDone = false;

      const tryAppend = () => {
        if (appending || sourceBuffer.updating || queue.length === 0) return;
        appending = true;
        sourceBuffer.appendBuffer(queue.shift()!);
      };

      sourceBuffer.addEventListener("updateend", () => {
        appending = false;
        if ((entry as any)._cancelled) { resolve(); return; }
        if (queue.length > 0) {
          tryAppend();
        } else if (streamDone) {
          mediaSource.endOfStream();
          resolve();
        }
      });

      sourceBuffer.addEventListener("error", reject);
      audio.play().catch(reject);

      reader = body.getReader();
      try {
        while (true) {
          if (isCancelled() || (entry as any)._cancelled) { resolve(); return; }
          const { done, value } = await reader.read();
          if (done) {
            streamDone = true;
            if (!appending && !sourceBuffer.updating && queue.length === 0) {
              mediaSource.endOfStream();
              resolve();
            }
            break;
          }
          queue.push(value.buffer as ArrayBuffer);
          tryAppend();
        }
      } catch (err) {
        resolve(); // cancelled reads throw — treat as clean stop
      }
    }, { once: true });
  });
}

async function playWithWebAudio(
  body: ReadableStream<Uint8Array>,
  audioCtx: AudioContext,
  entry: ActivePlayback,
  isCancelled: () => boolean,
): Promise<void> {
  const reader = body.getReader();
  entry.cancel = () => {
    reader.cancel().catch(() => {});
    entry.webAudioSource?.stop();
    (entry as any)._cancelled = true;
  };

  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    if (isCancelled() || (entry as any)._cancelled) return;
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.byteLength;
  }

  if (isCancelled() || (entry as any)._cancelled) return;

  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }

  const audioBuffer = await audioCtx.decodeAudioData(merged.buffer as ArrayBuffer);
  if (isCancelled() || (entry as any)._cancelled) return;

  const source = audioCtx.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(audioCtx.destination);
  entry.webAudioSource = source;
  source.start(0);
  await new Promise<void>((resolve) => { source.onended = () => resolve(); });
}